import { useCallback, useId, useMemo } from 'react';
import { Bar, BarChart, ResponsiveContainer } from 'recharts';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import { PollOptions } from '../../SlideComponents/slide.types';

type Props = {
  pollOptions: PollOptions[];
  // When false, ignore isCorrect and paint every bar in a neutral grey.
  // Used by the live-game host view during pre/countdown/in, where revealing
  // the answer key before responses are in would spoil the question.
  revealAnswers?: boolean;
};

const MultipleChoiceBars = ({ pollOptions, revealAnswers = true }: Props) => {
  // Recharts uses the BarChart id to generate internal <defs> ids (clipPath,
  // gradients). With multiple MC slides on the page at once — the editor
  // canvas plus thumbnails in the slide list — a hardcoded id collides and
  // one chart's clip ends up pointed at another chart's defs, blanking
  // bars. useId gives each mount a stable, unique id.
  const chartId = useId();

  const dataWithCount = useMemo(
    () => pollOptions.map(option => ({ ...option, count: 0 })),
    [pollOptions],
  );

  // Pass shape as a render function so dataWithCount + revealAnswers are
  // captured via closure. Using `shape={<MultipleChoiceBar … />}` made
  // recharts cloneElement those props with its own, which technically
  // gives precedence to the recharts-injected props on collision
  // (currently benign since `data` is the same array, but `revealAnswers`
  // survived only because recharts doesn't pass it). A closure removes
  // the ambiguity entirely. useCallback stabilises the reference so
  // recharts doesn't re-render every bar when only an unrelated parent
  // prop changes.
  const renderShape = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (props: any) => {
      const { index, width, height, x, y } = props;
      const option: PollOptions | undefined = dataWithCount[index];
      const fill = !revealAnswers
        ? theme.colors.greyLight
        : option?.isCorrect
        ? theme.colors.green
        : theme.colors.red;
      return (
        <rect
          x={x}
          y={y}
          width={width >= 0 ? width : 0}
          height={height >= 0 ? height : 0}
          rx="10"
          fill={fill}
          filter={'drop-shadow(rgba(0, 0, 0, 0.1) 0px -2px 3px)'}
        />
      );
    },
    [dataWithCount, revealAnswers],
  );

  if (!pollOptions.length) return null;

  return (
    <Container width="100%" height="100%">
      <BarChart
        id={chartId}
        data={dataWithCount}
        margin={{ top: 0, right: 10, left: 10, bottom: 10 }}
      >
        <Bar dataKey="count" shape={renderShape} minPointSize={5} />
      </BarChart>
    </Container>
  );
};

const Container = styled(ResponsiveContainer)`
  svg {
    overflow: visible;
  }
`;

export default MultipleChoiceBars;
